"use client";

import { useMemo, useState } from "react";
import { Search } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/ui/reveal";
import { SectionHeader } from "@/components/ui/section-header";
import { FAQ_ITEMS } from "./faq-items";

export function Faq() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_ITEMS;
    return FAQ_ITEMS.filter(
      (f) =>
        f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <section id="faq" className="relative py-24 md:py-28">
      <div className="mx-auto max-w-280 px-6 md:px-8">
        <SectionHeader tag="FAQ" heading="Common Questions" />

        <div className="mx-auto max-w-180">
          <Reveal>
            <div className="border-rl-border focus-within:border-rl-accent/40 bg-rl-bg2 mb-6 flex items-center gap-3 rounded-full border px-5 py-3 transition-colors">
              <Search size={15} className="text-rl-text3" aria-hidden />
              <label htmlFor="faq-search" className="sr-only">
                Search questions
              </label>
              <input
                id="faq-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions"
                aria-label="Search questions"
                className="text-foreground placeholder:text-rl-text3 flex-1 bg-transparent text-[0.875rem] outline-none"
              />
            </div>
          </Reveal>

          <Reveal delay={80}>
            {filtered.length === 0 ? (
              <div className="border-rl-border bg-rl-bg2 rounded-2xl border p-8 text-center">
                <p className="text-rl-text2 text-[0.9rem]">
                  No questions match &ldquo;{query}&rdquo;.
                </p>
                <p className="text-rl-text3 mt-1 text-[0.8125rem]">
                  Try a different keyword, or{" "}
                  <a
                    href="mailto:admin@raileon.com"
                    className="text-rl-accent hover:underline"
                  >
                    email us
                  </a>{" "}
                  directly.
                </p>
              </div>
            ) : (
              <div className="border-rl-border bg-rl-bg2 divide-rl-border overflow-hidden rounded-2xl border divide-y shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <Accordion type="single" collapsible>
                  {filtered.map((faq, i) => (
                    <AccordionItem
                      key={faq.q}
                      value={`item-${i}`}
                      className="border-0"
                    >
                      <AccordionTrigger className="hover:bg-rl-bg3 flex w-full cursor-pointer items-center justify-between px-6 py-5 text-left transition-colors">
                        <span className="text-foreground pr-4 text-[0.9375rem] font-semibold">
                          {faq.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-rl-text2 px-6 pb-5 text-[0.875rem] leading-[1.75]">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
